import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, MapPinIcon, BriefcaseIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { JobSearchParams } from '../types/job';
import { jobsApi } from '../services/api/jobs';
import { axiosInstance } from '../services/api/axios';

interface SearchState {
  query: string;
  location: string;
  limit: number;
  userId?: string;
  sortBy: 'recent' | 'relevant';
  datePosted?: '1' | '7' | '30' | 'all';
  experienceLevel?: 'entry' | 'associate' | 'mid-senior' | 'director';
  jobType?: 'full_time' | 'part_time' | 'contract' | 'temporary' | 'internship';
  company?: string;
  remote: boolean;
  easyApply: boolean;
  salary: boolean;
  minSalary?: number;
}

interface User {
  id: string;
  email: string;
  name: string;
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchParams, setSearchParams] = useState<SearchState>({
    query: '',
    location: 'Netherlands',
    limit: 10,
    userId: undefined,
    sortBy: 'recent',
    remote: false,
    easyApply: false,
    salary: false
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users');
        const userData = response.data.users || response.data;
        setUsers(Array.isArray(userData) ? userData : []);
        console.log('Fetched users:', userData);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!searchParams.query.trim()) {
      setValidationError('Please enter a job title or keyword');
      return;
    }

    try {
      await jobsApi.searchJobs({
        query: searchParams.query,
        location: searchParams.location,
        limit: searchParams.limit,
        userId: searchParams.userId,
        sortBy: searchParams.sortBy,
        datePosted: searchParams.datePosted,
        experienceLevel: searchParams.experienceLevel,
        jobType: searchParams.jobType,
        company: searchParams.company,
        remote: searchParams.remote,
        easyApply: searchParams.easyApply,
        salary: searchParams.salary,
        minSalary: searchParams.minSalary
      });

      const params = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });
      navigate(`/jobs?${params.toString()}`);
    } catch (error) {
      console.error('Search failed:', error);
      setValidationError('Failed to perform search. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSearchParams((prev: SearchState) => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 pt-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-5xl font-extrabold text-white mb-6 tracking-tight">
            Advanced Job Search
          </h1>
          <p className="text-xl text-blue-100 font-medium">
            Find your dream job with our powerful search tools
          </p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8">
            <div className="space-y-8">
              {/* Main Search Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Job Search Input */}
                <div className="relative">
                  <label htmlFor="query" className="block text-sm font-semibold text-gray-700 mb-2">
                    Job Title or Keywords
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BriefcaseIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <input
                      id="query"
                      type="text"
                      name="query"
                      value={searchParams.query}
                      onChange={handleInputChange}
                      placeholder="e.g., Software Engineer"
                      className={`block w-full pl-12 pr-4 py-3.5 border-2 ${
                        validationError ? 'border-red-500' : 'border-gray-200'
                      } rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300`}
                    />
                  </div>
                  {validationError && (
                    <p className="mt-2 text-sm text-red-600 font-medium">{validationError}</p>
                  )}
                </div>

                {/* Location Input */}
                <div className="relative">
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <input
                      id="location"
                      type="text"
                      name="location"
                      value={searchParams.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Amsterdam"
                      className="block w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    />
                  </div>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="border-t-2 border-gray-100 pt-8">
                <div className="flex items-center mb-6">
                  <AdjustmentsHorizontalIcon className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">Advanced Filters</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Results Limit */}
                  <div>
                    <label htmlFor="limit" className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Results
                    </label>
                    <select
                      id="limit"
                      name="limit"
                      value={searchParams.limit}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value={1}>1 results</option>
                      <option value={5}>5 results</option>
                      <option value={10}>10 results</option>
                      <option value={25}>25 results</option>
                      <option value={50}>50 results</option>
                      <option value={1000000}>1000000 results</option>
                    </select>
                  </div>

                  {/* User Selection */}
                  <div>
                    <label htmlFor="userId" className="block text-sm font-semibold text-gray-700 mb-2">
                      Search For User
                    </label>
                    <select
                      id="userId"
                      name="userId"
                      value={searchParams.userId || ''}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="">Select a user</option>
                      {users && users.length > 0 && users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Sort By */}
                  <div>
                    <label htmlFor="sortBy" className="block text-sm font-semibold text-gray-700 mb-2">
                      Sort By
                    </label>
                    <select
                      id="sortBy"
                      name="sortBy"
                      value={searchParams.sortBy}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="recent">Most Recent</option>
                      <option value="relevant">Most Relevant</option>
                    </select>
                  </div>

                  {/* Date Posted */}
                  <div>
                    <label htmlFor="datePosted" className="block text-sm font-semibold text-gray-700 mb-2">
                      Date Posted
                    </label>
                    <select
                      id="datePosted"
                      name="datePosted"
                      value={searchParams.datePosted}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="">Any Time</option>
                      <option value="1">Past 24 hours</option>
                      <option value="7">Past Week</option>
                      <option value="30">Past Month</option>
                    </select>
                  </div>

                  {/* Experience Level */}
                  <div>
                    <label htmlFor="experienceLevel" className="block text-sm font-semibold text-gray-700 mb-2">
                      Experience Level
                    </label>
                    <select
                      id="experienceLevel"
                      name="experienceLevel"
                      value={searchParams.experienceLevel}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="">Any Level</option>
                      <option value="internship">Internship</option>
                      <option value="entry">Entry Level</option>
                      <option value="associate">Associate</option>
                      <option value="mid-senior">Mid-Senior Level</option>
                      <option value="director">Director</option>
                    </select>
                  </div>

                  {/* Job Type */}
                  <div>
                    <label htmlFor="jobType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Job Type
                    </label>
                    <select
                      id="jobType"
                      name="jobType"
                      value={searchParams.jobType}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="">Any Type</option>
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="temporary">Temporary</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={searchParams.company || ''}
                      onChange={handleInputChange}
                      placeholder="Company name"
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    />
                  </div>

                  {/* Minimum Salary */}
                  <div>
                    <label htmlFor="minSalary" className="block text-sm font-semibold text-gray-700 mb-2">
                      Minimum Salary
                    </label>
                    <select
                      id="minSalary"
                      name="minSalary"
                      value={searchParams.minSalary || ''}
                      onChange={handleInputChange}
                      className="block w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm hover:border-blue-300"
                    >
                      <option value="">Any Salary</option>
                      <option value="40000">€40,000+</option>
                      <option value="60000">€60,000+</option>
                      <option value="80000">€80,000+</option>
                      <option value="100000">€100,000+</option>
                      <option value="120000">€120,000+</option>
                      <option value="140000">€140,000+</option>
                      <option value="160000">€160,000+</option>
                      <option value="180000">€180,000+</option>
                      <option value="200000">€200,000+</option>
                    </select>
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-xl hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      id="remote"
                      name="remote"
                      checked={searchParams.remote}
                      onChange={(e) => handleInputChange({
                        target: { name: 'remote', value: e.target.checked }
                      } as any)}
                      className="h-5 w-5 text-blue-600 rounded-md focus:ring-blue-500 border-gray-300 transition-colors"
                    />
                    <label htmlFor="remote" className="text-sm font-medium text-gray-700">
                      Remote Only
                    </label>
                  </div>

                  <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-xl hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      id="easyApply"
                      name="easyApply"
                      checked={searchParams.easyApply}
                      onChange={(e) => handleInputChange({
                        target: { name: 'easyApply', value: e.target.checked }
                      } as any)}
                      className="h-5 w-5 text-blue-600 rounded-md focus:ring-blue-500 border-gray-300 transition-colors"
                    />
                    <label htmlFor="easyApply" className="text-sm font-medium text-gray-700">
                      Easy Apply
                    </label>
                  </div>

                  <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-xl hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      id="salary"
                      name="salary"
                      checked={searchParams.salary}
                      onChange={(e) => handleInputChange({
                        target: { name: 'salary', value: e.target.checked }
                      } as any)}
                      className="h-5 w-5 text-blue-600 rounded-md focus:ring-blue-500 border-gray-300 transition-colors"
                    />
                    <label htmlFor="salary" className="text-sm font-medium text-gray-700">
                      Show Salary Only
                    </label>
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <div className="mt-8">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl flex items-center justify-center space-x-3 transform hover:-translate-y-0.5"
                >
                  <MagnifyingGlassIcon className="h-6 w-6" />
                  <span>Search Jobs</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SearchPage;